const sections = [
  {
    title: "1. Overview",
    body: [
      "This Cookie Policy explains how iBox uses cookies, local storage, session storage, and similar technologies when you use the iBox website, desktop application, documentation, and related services.",
      "This page should be read together with the iBox Privacy Policy, which explains how we collect, use, and protect information more broadly.",
    ],
  },
  {
    title: "2. What cookies are",
    body: [
      "Cookies are small files stored by your browser that help websites remember information about your visit. Similar technologies, such as local storage and session storage, can also store information on your device for functionality, preferences, or security.",
      "Some cookies are temporary and expire when you close your browser. Others remain for a longer period or until you delete them.",
    ],
  },
  {
    title: "3. How iBox uses cookies and storage",
    body: [
      "iBox uses cookies and similar technologies to operate the website, keep users signed in, support account and checkout functionality, remember preferences, protect against abuse, improve reliability, and provide a better user experience.",
      "The iBox desktop application may also store local settings, preferences, authentication state, app configuration, and related data on your device so the app can function properly.",
    ],
  },
  {
    title: "4. Essential cookies and storage",
    body: [
      "Essential cookies and storage are required for core functionality, such as authentication, security, session management, account access, checkout, and remembering basic app or website preferences.",
      "Because these are necessary for iBox to work, they cannot always be disabled through iBox settings. You may still block them through your browser, but doing so may cause parts of iBox to stop working correctly.",
    ],
  },
  {
    title: "5. Preference cookies and storage",
    body: [
      "Preference storage may be used to remember settings such as display preferences, selected pages, local configuration choices, saved app state, and other non-essential convenience settings.",
      "Removing preference data may reset parts of your iBox experience or require you to configure certain settings again.",
    ],
  },
  {
    title: "6. Analytics and diagnostics",
    body: [
      "iBox may use limited analytics or diagnostic technologies to understand website performance, app reliability, errors, crashes, and general usage patterns.",
      "When possible, we use aggregated or de-identified information to improve iBox without identifying individual users.",
    ],
  },
  {
    title: "7. Third-party cookies",
    body: [
      "Some third-party services used by iBox may set their own cookies or use similar technologies. For example, payment providers may use cookies to process subscriptions, prevent fraud, and complete checkout.",
      "Third-party services are governed by their own cookie and privacy policies. iBox does not control how third parties use cookies outside of the services they provide to iBox.",
    ],
  },
  {
    title: "8. Local app data",
    body: [
      "The iBox desktop app may store data locally on your device, including app preferences, ticker settings, module configurations, local asset references, cached integration data, and other information needed to provide the features you configure.",
      "Local app data is different from browser cookies. You can typically remove it by using app settings, resetting local data, uninstalling the app, or deleting application data through your operating system.",
    ],
  },
  {
    title: "9. Managing cookies",
    body: [
      "Most browsers allow you to view, delete, block, or limit cookies through browser settings. The exact controls depend on your browser and device.",
      "Blocking or deleting cookies may affect sign-in, checkout, saved preferences, documentation behavior, and other iBox functionality.",
    ],
  },
  {
    title: "10. Do Not Track",
    body: [
      "Some browsers offer a Do Not Track signal. There is no consistent industry standard for responding to these signals, so iBox does not currently respond to Do Not Track browser signals.",
    ],
  },
  {
    title: "11. Changes to this Cookie Policy",
    body: [
      "We may update this Cookie Policy from time to time. When we do, we will update the \"Last updated\" date. Continued use of iBox after an updated Cookie Policy becomes effective means you acknowledge the updated policy.",
    ],
  },
  {
    title: "12. Contact",
    body: [
      "Questions about this Cookie Policy can be sent through the contact page on the iBox website.",
    ],
  },
];

export function CookiesPage() {
  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-35" />
        <div className="absolute inset-0 bg-bg/40" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto max-w-4xl px-6 py-24">
        <div className="mb-12">
          <div className="mb-6 flex items-center gap-3">
            <div className="h-px w-10 bg-primary" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">Legal</span>
          </div>
          <h1 className="font-display text-6xl font-bold uppercase leading-[0.9] text-text md:text-8xl">Cookie Policy</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted md:text-lg">
            Last updated: May 26, 2026
          </p>
        </div>

        <div className="rounded-xl border border-border bg-surface/92 shadow-2xl shadow-black/20">
          <div className="border-b border-border p-6 md:p-8">
            <p className="text-base leading-relaxed text-muted">
              This policy explains the cookies and local storage technologies iBox uses and how you can manage them.
            </p>
          </div>
          <div className="divide-y divide-border">
            {sections.map((section) => (
              <section key={section.title} className="p-6 md:p-8">
                <h2 className="font-display text-2xl font-bold uppercase leading-none text-text md:text-3xl">{section.title}</h2>
                <div className="mt-4 space-y-4">
                  {section.body.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-7 text-muted md:text-base">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
